import { Bell, BookOpenText } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router';
import { useConges, useEspaceRh } from '@/api/hooks';
import { LIBELLE_ETAPE, LIBELLE_REGIME } from '@/api/types';
import { useSession } from '@/auth/session';
import { Chargement, ErreurBloc, PageHeader, Vide } from '@/components/common';
import { LienConge, StatutCongeBadge } from '@/components/rh';
import { MesConges } from './MesConges';
import { MesPrets } from './MesPrets';
import { MesProcedures } from '@/pages/procedures/MesProcedures';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { dateFr, montant } from '@/lib/utils';

const ONGLETS = ['conges', 'prets', 'demandes', 'notifications', 'procedures'] as const;
type Onglet = (typeof ONGLETS)[number];
const LIBELLE_ONGLET: Record<Onglet, string> = { conges: 'Mes congés', prets: 'Mes prêts sociaux', demandes: 'Mes demandes', notifications: 'Mes notifications', procedures: 'Mes procédures' };

const anciennete = (a: { annees: number; mois: number } | null): string => (a ? `${a.annees} an${a.annees > 1 ? 's' : ''} et ${a.mois} mois` : '—');

/**
 * Espace « Mes démarches RH » (spécifications démarches RH v1.4, § 3.1) : ouvert à tout agent actif, même sans profil.
 * Lot RH-1 : identité et situation RH reprises du référentiel ; lot RH-2 : congés ; lot RH-3 : prêts sociaux ;
 * lot RH-5 : « Mes procédures ».
 */
export function MesDemarches() {
  const session = useSession();
  const u = session!.utilisateur;
  const { onglet } = useParams();
  const navigate = useNavigate();
  // « /rh/prets » est la liste des prêts (onglets professionnels) : l'onglet personnel vit sous « /rh/mes-prets ».
  const slug = onglet === 'mes-prets' ? 'prets' : onglet;
  const courant: Onglet = ONGLETS.includes(slug as Onglet) ? (slug as Onglet) : 'conges';
  const { data, isLoading, error } = useEspaceRh();
  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const f = data.fiche;
  const disponible = { procedures: data.modules.procedures === 'DISPONIBLE' };
  return (
    <div className="space-y-6">
      <PageHeader titre="Mes démarches RH" sousTitre={`${f.fonction} · ${f.structure?.libelle ?? '—'} · ${f.matricule}${f.grade ? ` · ${f.grade}` : ''}`} />

      <Card data-testid="identite-rh">
        <CardHeader>
          <CardTitle>Ma situation administrative</CardTitle>
          <CardDescription>Reprise du référentiel des agents, tenue par les Ressources humaines : signalez-leur toute erreur, rien ne se saisit ici.</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
          <div><span className="text-muted-foreground">Matricule</span><div>{f.matricule}</div></div>
          <div><span className="text-muted-foreground">Grade</span><div>{f.grade ?? '—'}</div></div>
          <div><span className="text-muted-foreground">Direction</span><div>{f.structure ? `${f.structure.code} · ${f.structure.libelle}` : '—'}</div></div>
          <div><span className="text-muted-foreground">Supérieur hiérarchique</span><div>{f.superieur ? `${f.superieur.nom} · ${f.superieur.fonction}` : ['DIR', 'SDIR', 'DG', 'DGA'].includes(f.categorie) ? 'Membre de la Direction Générale superviseur' : 'À renseigner par les Ressources humaines'}</div></div>
          <div><span className="text-muted-foreground">Date d'engagement</span><div>{f.dateEngagement ? dateFr(f.dateEngagement) : 'À renseigner par les RH'}</div></div>
          <div><span className="text-muted-foreground">Ancienneté</span><div>{anciennete(f.anciennete)}{f.dateAnciennete ? ` (reconnue depuis le ${dateFr(f.dateAnciennete)})` : ''}</div></div>
          <div><span className="text-muted-foreground">Période de droits en cours</span><div>{f.periodeEnCours ? `du ${dateFr(f.periodeEnCours.debut)} au ${dateFr(f.periodeEnCours.fin)}` : '—'}</div></div>
          <div>
            <span className="text-muted-foreground">Régime de congé annuel</span>
            <div>
              {f.droitAnnuel ? (
                <>
                  {LIBELLE_REGIME[f.droitAnnuel.regime]}
                  <div className="text-muted-foreground">
                    {f.droitAnnuel.base} jours + {f.droitAnnuel.majoration} d'ancienneté = <span className="font-medium text-foreground">{f.droitAnnuel.total} jours ouvrables</span>
                  </div>
                </>
              ) : (
                <Badge variant="outline">À confirmer par les RH</Badge>
              )}
            </div>
          </div>
          <div>
            <span className="text-muted-foreground">Droit au congé annuel</span>
            <div>{f.douzeMoisDeService ? 'Ouvert (douze mois de service accomplis)' : 'Ouvert après douze mois de service'}</div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={courant} onValueChange={(v) => navigate(v === 'conges' ? '/rh' : v === 'prets' ? '/rh/mes-prets' : `/rh/${v}`)}>
        <TabsList>
          {ONGLETS.map((o) => (
            <TabsTrigger key={o} value={o}>
              {LIBELLE_ONGLET[o]}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {courant === 'conges' && <MesConges />}
      {courant === 'prets' && (
        <div className="space-y-4">
          <MesPrets />
          {data.situation && (
            <Card>
              <CardHeader>
                <CardTitle>Ma situation confidentielle</CardTitle>
                <CardDescription>Visible de vous et des Ressources humaines ; sert au calcul du plafond d'un prêt (version {data.situation.version}, enregistrée le {dateFr(data.situation.date)}).</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 text-sm sm:grid-cols-3">
                <div><span className="text-muted-foreground">Salaire net contractuel</span><div>{data.situation.salaireNet === null ? '—' : `${data.situation.salaireNet.toLocaleString('fr-FR')} ${data.situation.devise}`}</div></div>
                <div><span className="text-muted-foreground">Départ à la retraite prévu</span><div>{data.situation.dateRetraitePrevue ? dateFr(data.situation.dateRetraitePrevue) : '—'}</div></div>
                <div><span className="text-muted-foreground">Décompte final estimé</span><div>{data.situation.decompteFinalEstime === null ? '—' : `${montant(data.situation.decompteFinalEstime)}${data.situation.dateCalculDecompte ? ` (calculé le ${dateFr(data.situation.dateCalculDecompte)})` : ''}`}</div></div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
      {courant === 'demandes' && <MesDemandesRh />}
      {courant === 'notifications' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="size-5" /> Mes notifications
            </CardTitle>
            <CardDescription>Les notifications des démarches RH rejoignent vos notifications habituelles.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild variant="outline">
              <Link to="/notifications">Ouvrir mes notifications</Link>
            </Button>
          </CardContent>
        </Card>
      )}
      {courant === 'procedures' && (disponible.procedures ? <MesProcedures /> : <AVenir icone={<BookOpenText className="size-5" />} titre="Mes procédures" disponible={false}>Bibliothèque des procédures non encore ouverte.</AVenir>)}
      {!u.profils.length && <p className="text-sm text-muted-foreground">Votre compte n'a pas d'autre module : cet espace et vos notifications sont les vôtres.</p>}
    </div>
  );
}

/** Toutes les demandes de l'agent, quel qu'en soit le statut ; les prêts s'y ajoutent avec le lot RH-3. */
function MesDemandesRh() {
  const { data, isLoading, error } = useConges({ onglet: 'mes' });
  if (isLoading) return <Chargement />;
  if (error) return <ErreurBloc erreur={error} />;
  return (
    <Card data-testid="mes-demandes-rh">
      <CardHeader>
        <CardTitle>Mes demandes</CardTitle>
        <CardDescription>Vos demandes de congé, leur statut et l'étape en cours. Les demandes de prêt social s'y ajouteront avec le lot RH-3.</CardDescription>
      </CardHeader>
      <CardContent>
        {!data?.length ? (
          <Vide message="Aucune demande pour le moment." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Numéro</TableHead>
                <TableHead>Objet</TableHead>
                <TableHead>Dates</TableHead>
                <TableHead className="text-right">Jours</TableHead>
                <TableHead>Statut</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <LienConge id={c.id} numero={c.numero} />
                  </TableCell>
                  <TableCell>{c.libelleCategorie}</TableCell>
                  <TableCell>
                    du {dateFr(c.dateDebut)} au {dateFr(c.dernierJour)}
                  </TableCell>
                  <TableCell className="num text-right">{c.joursPris ?? c.joursOuvrables}</TableCell>
                  <TableCell className="space-x-1">
                    <StatutCongeBadge statut={c.statut} />
                    {c.statut === 'SOUMIS' && c.etapeCourante && <Badge variant="secondary">{LIBELLE_ETAPE[c.etapeCourante]}</Badge>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function AVenir({ icone, titre, disponible, children }: { icone: React.ReactNode; titre: string; disponible: boolean; children: React.ReactNode }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {icone} {titre} {!disponible && <Badge variant="outline">à venir</Badge>}
        </CardTitle>
        <CardDescription>{children}</CardDescription>
      </CardHeader>
    </Card>
  );
}
